import {
  __name,
  __spreadValues
} from "./chunk-CRQHCYH4.js";

// src/util/collections.ts
import React from "react";
var pickChild = /* @__PURE__ */ __name((children, targetChild) => {
  const target = [];
  const withoutTargetChildren = React.Children.map(children, (item) => {
    if (!React.isValidElement(item))
      return item;
    if (item.type === targetChild) {
      target.push(item);
      return null;
    }
    return item;
  });
  const targetChildren = target.length >= 0 ? target : void 0;
  return [withoutTargetChildren, targetChildren];
}, "pickChild");
var uniq = /* @__PURE__ */ __name((array) => {
  if (!array) {
    return [];
  }
  return array.filter((v, i, a) => a.indexOf(v) === i);
}, "uniq");
function filterMissing(value) {
  if (value === null || value === void 0)
    return false;
  return true;
}
__name(filterMissing, "filterMissing");

// src/util/colors.ts
var hexToRgb = /* @__PURE__ */ __name((hex) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r1, g1, b1) => r1 + r1 + g1 + g1 + b1 + b1);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const [, r, g, b] = result;
    return [r ? parseInt(r, 16) : 0, g ? parseInt(g, 16) : 0, b ? parseInt(b, 16) : 0];
  }
  return [0, 0, 0];
}, "hexToRgb");
var isColorDark = /* @__PURE__ */ __name((red, green, blue) => {
  const [r, g, b] = [red, green, blue].map((c) => c / 255).map((c) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * (r || 0) + 0.7152 * (g || 0) + 0.0722 * (b || 0);
  return luminance <= 0.179;
}, "isColorDark");

// src/util/objects.ts
var dig = /* @__PURE__ */ __name((object, search) => {
  search = search.replace(/\[(\w+)\]/g, ".$1");
  search = search.replace(/^\./, "");
  const parts = search.split(".");
  for (let i = 0, n = parts.length; i < n; ++i) {
    const key = parts[i];
    if (key && key in object) {
      object = object[key];
    } else {
      return;
    }
  }
  return object;
}, "dig");
var objEmpty = /* @__PURE__ */ __name((obj) => !obj || Object.keys(obj).length === 0, "objEmpty");
var objExclude = /* @__PURE__ */ __name((obj, ...keys) => {
  const newObj = __spreadValues({}, obj);
  keys.forEach((key) => delete newObj[key]);
  return newObj;
}, "objExclude");
var objOnly = /* @__PURE__ */ __name((obj, ...keys) => {
  const newObj = __spreadValues({}, obj);
  Object.keys(newObj).forEach((key) => {
    if (!keys.includes(key)) {
      delete newObj[key];
    }
  });
  return newObj;
}, "objOnly");

// src/util/react.ts
import "react";
function mergeRefs(...refs) {
  return (value) => {
    for (const ref of refs) {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref) {
        ;
        ref.current = value;
      }
    }
  };
}
__name(mergeRefs, "mergeRefs");

// src/util/size.ts
var sizeHuman = /* @__PURE__ */ __name((size) => {
  if (size < 1024) {
    return `${size} B`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else if (size < 1024 * 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  } else {
    return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
}, "sizeHuman");

// src/util/string.ts
var isBlank = /* @__PURE__ */ __name((text) => !text || text.length === 0 || !text.trim(), "isBlank");

// src/util/time.ts
var durationHuman = /* @__PURE__ */ __name((duration, short) => {
  const perMinute = 60;
  const perHour = perMinute * 60;
  if (duration < perMinute) {
    return Math.round(duration) + "s";
  } else if (duration < perHour) {
    const spare = duration - Math.floor(duration / perMinute) * perMinute;
    if (short)
      return Math.round(duration / perMinute) + "m";
    const spareText = spare > 0 ? `${spare}s` : "";
    return Math.round(duration / perMinute) + "m" + spareText;
  } else {
    const spare = duration - Math.floor(duration / perHour) * perHour;
    if (short)
      return Math.round(duration / perHour) + "hr";
    const spareText = spare > 0 ? `${Math.round(spare / perMinute)}m` : "";
    return Math.round(duration / perHour) + "hr" + spareText;
  }
}, "durationHuman");
var sinceHuman = /* @__PURE__ */ __name((since, short) => {
  const duration = ((/* @__PURE__ */ new Date()).getTime() - (typeof since === "number" ? since : Date.parse(since))) / 1e3;
  const perMinute = 60;
  const perHour = perMinute * 60;
  const perDay = perHour * 24;
  const perYear = perDay * 365;
  const suffix = short ? "" : " ago";
  if (duration < perMinute) {
    return Math.round(duration) + "s" + suffix;
  } else if (duration < perHour) {
    return Math.round(duration / perMinute) + "m" + suffix;
  } else if (duration < perDay) {
    const spare = duration - Math.floor(duration / perHour) * perHour;
    if (short)
      return Math.round(duration / perHour) + "hr";
    const spareText = spare > 0 ? `${Math.round(spare / perMinute)}m` : "";
    return Math.round(duration / perHour) + "hr" + spareText + suffix;
  } else if (duration < perYear) {
    const spare = duration - Math.floor(duration / perDay) * perDay;
    if (short)
      return Math.round(duration / perDay) + "d";
    const spareText = spare > 0 ? `${Math.round(spare / perHour)}hr` : "";
    return Math.round(duration / perDay) + "d" + spareText + suffix;
  } else {
    const spare = duration - Math.floor(duration / perYear) * perYear;
    const spareText = spare > 0 ? `${Math.round(spare / perDay)}d` : "";
    return Math.round(duration / perYear) + "y" + spareText + suffix;
  }
}, "sinceHuman");
var timeHuman = /* @__PURE__ */ __name((since, capitalize) => {
  const now = /* @__PURE__ */ new Date();
  const date = new Date(typeof since === "number" ? since : Date.parse(since));
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if ((now.getTime() - date.getTime()) / 1e3 < 60) {
    return capitalize ? "Just now" : "just now";
  }
  let day = date.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
  if (date.getFullYear() === now.getFullYear()) {
    day = date.toLocaleDateString([], { month: "short", day: "numeric" });
    if (date.getDate() === now.getDate() && date.getMonth() === now.getMonth()) {
      day = "today";
    }
    if (date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth()) {
      day = "yesterday";
    }
  }
  if (capitalize) {
    day = day.charAt(0).toLocaleUpperCase() + day.substring(1);
  }
  return `${day} at ${date.toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit" })}`;
}, "timeHuman");

export {
  pickChild,
  uniq,
  filterMissing,
  hexToRgb,
  isColorDark,
  dig,
  objEmpty,
  objExclude,
  objOnly,
  mergeRefs,
  sizeHuman,
  isBlank,
  durationHuman,
  sinceHuman,
  timeHuman
};
//# sourceMappingURL=chunk-Y3WXUM7H.js.map